import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';
import { Company, Client } from '../../interfaces/anagraphic.interfaces';
import {
  ShipmentRequestService,
  Waybill,
  Parcel,
  Dealer,
  Brand,
  Shipment,
  WaybillStatus,
  ParcelStatus,
  WaybillDeliveryMethod,
  WaybillDeliveryType,
  DealerStatus,  BrandStatus
} from '../../services/shipment-request.service';

// Extend Waybill interface for reissue functionality
interface ReissueWaybill extends Waybill {
  waybillOriginCode?: string;
  parcels?: Parcel[];
}
import { LabelGenerationService } from '../../services/label-generation.service';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-reissue',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, NgxPaginationModule],
  templateUrl: './reissue.component.html',
  styleUrl: './reissue.component.css'
})
export class ReissueComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('reissueDateInput', { static: false }) reissueDateInput!: ElementRef;
  reissueForm: FormGroup;
  isFormCollapsed = false; // Stato del form (aperto/chiuso)

  // Make Math available in template
  Math = Math;
  // Main data arrays
  originalWaybills: Waybill[] = []; // Bolle originali da ri-emettere
  reissuedWaybills: ReissueWaybill[] = []; // Bolle ri-emesse
  parcels: Parcel[] = [];
  dealers: Dealer[] = [];
  brands: Brand[] = [];

  // Pagination properties
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  // Filter properties
  searchTerm = '';
  selectedDealerFilter = '';
  selectedBrandFilter = '';
  selectedStatusFilter = '';
  selectedDateRange = { start: '', end: '' };

  // Sort properties
  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // UI State
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  isSubmitting = false;

  // Selection state
  selectedWaybillsForReissue: string[] = [];
  selectAllChecked = false;

  // View mode
  viewMode: 'search' | 'reissue' = 'search'; // search per cercare bolle, reissue per ri-emettere

  // Constants for status options
  readonly WAYBILL_STATUSES = Object.values(WaybillStatus);
  readonly DELIVERY_METHODS = Object.values(WaybillDeliveryMethod);  readonly DELIVERY_TYPES = Object.values(WaybillDeliveryType);

  constructor(
    private formBuilder: FormBuilder,
    private shipmentService: ShipmentRequestService,
    private labelService: LabelGenerationService,
    private translate: TranslateService,
    private http: HttpClient
  ) {
    this.reissueForm = this.createReissueForm();
  }

  ngOnInit(): void {
    this.loadInitialData();
    this.loadOriginalWaybills();
  }

  ngAfterViewInit(): void {
    this.initializeDatePicker();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private createReissueForm(): FormGroup {
    return this.formBuilder.group({
      // Criteri di ricerca per bolle originali
      searchCriteria: this.formBuilder.group({
        waybillCode: [''],
        dealerCode: [''],
        brandCode: [''],
        dateFrom: [''],
        dateTo: [''],
        status: ['']
      }),
      
      // Dati per la ri-emissione
      reissueData: this.formBuilder.group({
        newDeliveryDate: ['', Validators.required],
        newDeliveryMethod: ['', Validators.required],
        newDeliveryType: ['', Validators.required],
        reason: ['', [Validators.required, Validators.minLength(10)]],
        notes: ['']
      })
    });
  }

  private loadInitialData(): void {
    this.isLoading = true;
    
    // Load dealers and brands for filters
    Promise.all([
      //this.loadDealers(),
      this.loadBrands()
    ]).finally(() => {
      this.isLoading = false;
    });
  }

  // private async loadDealers(): Promise<void> {
  //   try {
  //     const dealers = await this.shipmentService.getDealers().toPromise();
  //     this.dealers = dealers || [];
  //   } catch (error) {
  //     console.error('Error loading dealers:', error);
  //   }
  // }

  private async loadBrands(): Promise<void> {
    try {
      const brands = await this.shipmentService.getBrands().toPromise();
      this.brands = brands || [];
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  }

  private loadOriginalWaybills(): void {
    this.isLoading = true;
    
      this.shipmentService.getWaybills([]).subscribe({
      next: (waybills) => {
        // Filtra solo le bolle che possono essere ri-emesse (ad esempio, quelle consegnate o con problemi)
        this.originalWaybills = waybills.filter(w => 
          w.waybillStatus === WaybillStatus.DELIVERED || 
          w.waybillStatus === WaybillStatus.RETURNED ||
          w.waybillStatus === WaybillStatus.PROBLEM
        );
        this.totalItems = this.originalWaybills.length;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading waybills:', error);
        this.errorMessage = this.translate.instant('REISSUE.ERRORS.LOAD_WAYBILLS');
        this.isLoading = false;
      }
    });
  }

  private initializeDatePicker(): void {
    if (this.reissueDateInput) {
      flatpickr(this.reissueDateInput.nativeElement, {
        locale: Italian,
        dateFormat: 'Y-m-d',
        allowInput: true,
        minDate: 'today',
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            const formattedDate = selectedDates[0].toISOString().split('T')[0];
            this.reissueForm.get('reissueData.newDeliveryDate')?.setValue(formattedDate);
          }
        }
      });
    }
  }

  // Form and UI Methods
  toggleFormCollapse(): void {
    this.isFormCollapsed = !this.isFormCollapsed;
  }

  switchViewMode(mode: 'search' | 'reissue'): void {
    this.viewMode = mode;
    this.clearMessages();
    
    if (mode === 'search') {
      this.selectedWaybillsForReissue = [];
      this.selectAllChecked = false;
    }
  }

  // Search and Filter Methods
  onSearchChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  private applyFilters(): void {
    const criteria = this.reissueForm.get('searchCriteria')?.value;
    
    let filtered = this.originalWaybills;

    // Apply search term
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(w => 
        w.waybillCode.toLowerCase().includes(term) ||
        w.waybillDealerCode?.toLowerCase().includes(term) ||
        w.waybillBrandCode?.toLowerCase().includes(term)
      );
    }

    // Apply form filters
    if (criteria.waybillCode) {
      filtered = filtered.filter(w => 
        w.waybillCode.toLowerCase().includes(criteria.waybillCode.toLowerCase())
      );
    }

    if (criteria.dealerCode) {
      filtered = filtered.filter(w => w.waybillDealerCode === criteria.dealerCode);
    }

    if (criteria.brandCode) {
      filtered = filtered.filter(w => w.waybillBrandCode === criteria.brandCode);
    }

    if (criteria.status) {
      filtered = filtered.filter(w => w.waybillStatus === criteria.status);
    }    // Apply date range
    if (criteria.dateFrom) {
      filtered = filtered.filter(w => 
        w.waybillDate && new Date(w.waybillDate) >= new Date(criteria.dateFrom)
      );
    }

    if (criteria.dateTo) {
      filtered = filtered.filter(w => 
        w.waybillDate && new Date(w.waybillDate) <= new Date(criteria.dateTo)
      );
    }

    this.originalWaybills = filtered;
    this.totalItems = filtered.length;
  }

  // Selection Methods
  toggleSelectAll(): void {
    this.selectAllChecked = !this.selectAllChecked;
    
    if (this.selectAllChecked) {
      this.selectedWaybillsForReissue = this.getFilteredWaybills().map(w => w.waybillCode);
    } else {
      this.selectedWaybillsForReissue = [];
    }
  }

  toggleWaybillSelection(waybillCode: string): void {
    const index = this.selectedWaybillsForReissue.indexOf(waybillCode);
    
    if (index > -1) {
      this.selectedWaybillsForReissue.splice(index, 1);
    } else {
      this.selectedWaybillsForReissue.push(waybillCode);
    }

    // Update select all checkbox
    const filteredWaybills = this.getFilteredWaybills();
    this.selectAllChecked = filteredWaybills.length > 0 && 
      filteredWaybills.every(w => this.selectedWaybillsForReissue.includes(w.waybillCode));
  }

  isWaybillSelected(waybillCode: string): boolean {
    return this.selectedWaybillsForReissue.includes(waybillCode);
  }

  // Reissue Methods
  onSubmit(): void {
    if (this.reissueForm.invalid) {
      this.markFormGroupTouched(this.reissueForm);
      this.errorMessage = this.translate.instant('REISSUE.ERRORS.FORM_INVALID');
      return;
    }

    if (this.selectedWaybillsForReissue.length === 0) {
      this.errorMessage = this.translate.instant('REISSUE.ERRORS.NO_WAYBILLS_SELECTED');
      return;
    }

    this.performReissue();
  }

  private performReissue(): void {
    this.isSubmitting = true;
    this.clearMessages();

    const reissueData = this.reissueForm.get('reissueData')?.value;
    const selectedWaybills = this.originalWaybills.filter(w => 
      this.selectedWaybillsForReissue.includes(w.waybillCode)
    );

    // Simulate reissue process
    setTimeout(() => {
      try {
        // Create new waybills with updated data
        const newWaybills = selectedWaybills.map(original => ({
          ...original,
          waybillCode: this.generateNewWaybillCode(original.waybillCode),
          waybillDate: new Date().toISOString(),
          waybillEstimatedDeliveryDate: reissueData.newDeliveryDate,
          waybillDeliveryMethod: reissueData.newDeliveryMethod,
          waybillDeliveryType: reissueData.newDeliveryType,
          waybillStatus: WaybillStatus.PENDING,
          waybillNote: `${original.waybillNote || ''}\n[REISSUE] ${reissueData.reason}`.trim(),
          waybillOriginCode: original.waybillCode, // Reference to original
          waybillLastUpdateAt: new Date().toISOString()
        }));

        this.reissuedWaybills = newWaybills;
        this.successMessage = this.translate.instant('REISSUE.SUCCESS.WAYBILLS_REISSUED', { 
          count: newWaybills.length 
        });

        // Switch to reissue view to show results
        this.viewMode = 'reissue';
        
        // Reset selection
        this.selectedWaybillsForReissue = [];
        this.selectAllChecked = false;

        // Reset form
        this.reissueForm.get('reissueData')?.reset();

      } catch (error) {
        console.error('Error during reissue:', error);
        this.errorMessage = this.translate.instant('REISSUE.ERRORS.REISSUE_FAILED');
      } finally {
        this.isSubmitting = false;
      }
    }, 2000); // Simulate processing time
  }

  private generateNewWaybillCode(originalCode: string): string {
    const timestamp = Date.now().toString().slice(-6);
    return `${originalCode}-R${timestamp}`;
  }

  // Utility Methods
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.controls[key];
      control.markAsTouched();
      
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  getFilteredWaybills(): Waybill[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.originalWaybills.slice(startIndex, endIndex);
  }

  // Label Generation
  generateLabelsForReissuedWaybills(): void {
    if (this.reissuedWaybills.length === 0) {
      this.errorMessage = this.translate.instant('REISSUE.ERRORS.NO_REISSUED_WAYBILLS');
      return;
    }

    this.isLoading = true;
    
    // Generate labels for all reissued waybills
    this.reissuedWaybills.forEach(waybill => {
      if (waybill.parcels && waybill.parcels.length > 0) {
        this.labelService.generateWaybillLabels(waybill.parcels, waybill).subscribe({
          next: (success) => {
            if (success) {
              console.log(`Labels generated for waybill: ${waybill.waybillCode}`);
            }
          },
          error: (error) => {
            console.error('Error generating labels:', error);
          }
        });
      }
    });

    this.isLoading = false;
    this.successMessage = this.translate.instant('REISSUE.SUCCESS.LABELS_GENERATED');
  }

  // Export functionality
  exportReissuedWaybills(): void {
    if (this.reissuedWaybills.length === 0) {
      this.errorMessage = this.translate.instant('REISSUE.ERRORS.NO_REISSUED_WAYBILLS');
      return;
    }    const exportData = this.reissuedWaybills.map(waybill => ({
      'Codice Bolla': waybill.waybillCode,
      'Bolla Originale': waybill.waybillOriginCode || '',
      'Data Emissione': waybill.waybillDate ? new Date(waybill.waybillDate).toLocaleDateString('it-IT') : '',
      'Data Consegna Prevista': waybill.waybillEstimatedDeliveryDate ? new Date(waybill.waybillEstimatedDeliveryDate).toLocaleDateString('it-IT') : '',
      'Metodo Consegna': waybill.waybillDeliveryMethod,
      'Tipo Consegna': waybill.waybillDeliveryType,
      'Dealer': waybill.waybillDealerCode,
      'Brand': waybill.waybillBrandCode,
      'Stato': waybill.waybillStatus,
      'Note': waybill.waybillNote
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bolle Riemesse');

    const fileName = `bolle_riemesse_${new Date().toISOString().split('T')[0]}.xlsx`;
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, fileName);
    this.successMessage = this.translate.instant('REISSUE.SUCCESS.EXPORT_COMPLETED');
  }

  // Pagination
  onPageChange(page: number): void {
    this.currentPage = page;
  }

  // Form field validation helpers
  isFieldInvalid(fieldPath: string): boolean {
    const field = this.reissueForm.get(fieldPath);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldPath: string): string {
    const field = this.reissueForm.get(fieldPath);
    
    if (field?.errors) {
      if (field.errors['required']) {
        return this.translate.instant('REISSUE.VALIDATION.REQUIRED');
      }
      if (field.errors['minlength']) {
        return this.translate.instant('REISSUE.VALIDATION.MIN_LENGTH', { 
          min: field.errors['minlength'].requiredLength 
        });
      }
    }
    
    return '';
  }

  // Helper methods for template
  formatDate(date: string | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('it-IT');
  }

  getStatusClass(status: WaybillStatus): string {
    switch (status) {
      case WaybillStatus.PENDING: return 'badge bg-warning';
      case WaybillStatus.IN_TRANSIT: return 'badge bg-info';
      case WaybillStatus.DELIVERED: return 'badge bg-success';
      case WaybillStatus.PROBLEM: return 'badge bg-danger';
      case WaybillStatus.RETURNED: return 'badge bg-secondary';
      case WaybillStatus.CANCELLED: return 'badge bg-dark';
      default: return 'badge bg-light';
    }
  }
  // Track by functions for ngFor
  trackByWaybillCode(index: number, waybill: Waybill): string {
    return waybill.waybillCode;
  }

  trackByReissueWaybillCode(index: number, waybill: ReissueWaybill): string {
    return waybill.waybillCode;
  }

  // Safe status getter for template
  getWaybillStatus(waybill: Waybill | ReissueWaybill): WaybillStatus {
    return waybill.waybillStatus || WaybillStatus.PENDING;
  }
}
